
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { TrendingUp, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import { getEnhancedPrimaryList } from "@/services/shopping/lists/primary-list-enhanced.service";
import { getMostPurchasedProducts, type MostPurchasedProduct } from "@/services/shopping/mostPurchased.service";
import { addShoppingItem } from "@/services/shopping/items/index";
import { queryClient } from '@/services/queryClient';
import { safeToastSuccess, safeToastError } from '@/components/ui/toastManager';
import { validateQuantity } from "@/utils/security";

export const MostPurchasedSection = () => {
  const { theme } = useThemeConfig();
  const [products, setProducts] = useState<MostPurchasedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline] = useState(networkService.isOnline());

  useEffect(() => {
    loadMostPurchasedProducts();
  }, []);

  const handleAddToPrimaryList = async (product: MostPurchasedProduct) => {
    try {
      const primaryList = await getEnhancedPrimaryList();
      if (!primaryList?.id) {
        OfflineLogger.error('MostPurchasedSection', 'Primary list not found');
        safeToastError('Impossibile aggiungere il prodotto alla lista');
        return;
      }

      const quantityResult = validateQuantity('1');

      if (!quantityResult.isValid) {
        OfflineLogger.error('MostPurchasedSection', 'Quantità predefinita non valida');
        safeToastError('Quantità non valida per il prodotto');
        return;
      }

      const itemData = {
        product_id: product.product_id,
        quantity: quantityResult.sanitized,
        unit: '',
        category: product.category_id ?? 'other',
        attributes: []
      };

      // ✅ FIX: Corretto numero di argomenti per addShoppingItem
      const added = await addShoppingItem(primaryList.id, itemData);
      if (added) {
        //OfflineLogger.info('MostPurchasedSection', { message: '✅ Aggiunto alla lista primaria', item: added });
        safeToastSuccess('Prodotto aggiunto alla lista della spesa');
        await queryClient.invalidateQueries({ queryKey: ['enhanced-primary-list'] });
        await queryClient.invalidateQueries({ queryKey: ['shopping-items', primaryList.id] });
      } else {
        safeToastError('Impossibile aggiungere il prodotto alla lista');
      }
    } catch (err) {
      OfflineLogger.error('MostPurchasedSection.handleAddToPrimaryList', err);
      safeToastError('Impossibile aggiungere il prodotto alla lista');
    }
  };

  const loadMostPurchasedProducts = async () => {
    try {
      setIsLoading(true);
      setProducts(await getMostPurchasedProducts());
    } catch (error) {
      OfflineLogger.error('MostPurchasedSection.loadMostPurchasedProducts', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="mb-6" style={{
        borderRadius: theme.borderRadius || '0.5rem',
        backgroundColor: theme.cardBackgroundColor || '#ffffff'
      }}>
        <CardContent className="pt-6">
          <p
            className="text-center text-sm"
            style={{ color: theme.sectionTextColor }}
          >
            Caricamento prodotti più acquistati...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (products.length === 0) {
    return (
      <Card
        className="mb-6"
        style={{
          borderRadius: theme.borderRadius || '0.5rem',
          backgroundColor: theme.cardBackgroundColor || '#ffffff',
          color: theme.sectionTitleColor || '#ffffff'
        }}
      >
        <CardHeader>
          <CardTitle
            className="flex items-center gap-2 text-lg font-semibold"
            style={{ color: theme.sectionTitleColor }}
          >
            <TrendingUp
              className="h-5 w-5"
              style={{ color: theme.primaryColor }}
            />
            Prodotti Più Acquistati
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm" style={{ color: theme.sectionTextColor }}>
            {!isOnline
              ? "Nessun dato disponibile offline."
              : "Inizia a fare la spesa per vedere i tuoi prodotti preferiti!"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="mb-2"
      style={{
        borderRadius: theme.borderRadius || '0.5rem',
        backgroundColor: theme.cardBackgroundColor || '#ffffff',
        color: theme.sectionTitleColor || '#ffffff'
      }}
    >
      <CardHeader className="pb-2">
        <CardTitle
          className="flex items-center gap-2 text-lg font-semibold"
          style={{ color: theme.sectionTitleColor }}
        >
          <TrendingUp
            className="h-5 w-5"
            style={{ color: theme.primaryColor }}
          />
          Prodotti Più Acquistati
        </CardTitle>
        <p
          className="text-sm mt-1 ml-7"
          style={{ color: theme.sectionTextColor }}
        >
          Ultimi 30 giorni
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          {products.map((product) => (
            <div
              key={product.product_id}
              className="rounded-lg px-2 py-3 shadow-sm flex flex-col justify-between h-full"
                style={{
                  backgroundColor: theme.cardBackgroundColor || '#ffffff',
                  border: `1px solid ${theme.borderColor || '#e2e8f0'}`
                }}
              >
                <div className="flex-1">
                  <p
                    className="text-xs font-medium line-clamp-2"
                    style={{ color: theme.sectionTextColor }}
                  >
                    {product.product_name || <span className="italic text-gray-400">Senza nome</span>}
                  </p>
                </div>
                <div className="mt-1.5 text-xs flex items-center justify-between" style={{
      color: theme.textColor,
    }}>
                  <span className="whitespace-nowrap" style={{ color: theme.sectionTextColor }}>
                    <span className="font-semibold ">{product.count}</span>{" "}
                    {product.count === 1 ? "volta" : "volte"}
                  </span>
                  <Button
                    size="sm"
                    className="h-6 w-6 p-0 rounded-full"
                    style={{
                      backgroundColor: product.category_color || theme.cardBackgroundColor,
                      color: product.category_text_color || theme.primaryColor,
                      borderColor: product.category_color || theme.primaryColor
                    }}
                    onClick={() => handleAddToPrimaryList(product)}
                  >
                    <ShoppingCart className="h-3 w-3" />
                    <span className="sr-only">Aggiungi alla lista</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
    </Card>
  );
};

export default MostPurchasedSection;
